import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Text } from '@components/Restyle';
import theme from '@themes/theme';

export type BottomToastHandle = {
  show: (
    message: string,
    options?: { duration?: number; color?: string; icon?: 'warning' }
  ) => void;
};

const WarningIcon = () => (
  <svg
    width={16}
    height={16}
    viewBox='0 0 24 24'
    fill='none'
    stroke={theme.colors.alertRed}
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    style={{ flexShrink: 0 }}>
    <path d='M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </svg>
);

/**
 * A `position: fixed` toast portaled directly onto `document.body`, bypassing
 * React's component tree entirely.
 *
 * `react-native-toast-notifications`' own containers rely on `position:
 * fixed`, which is normally relative to the viewport — except CSS defines
 * that any ancestor with a `transform` other than `none` becomes the
 * containing block for its `position: fixed` descendants instead. Two
 * unrelated things in this app apply such a transform to screens that host
 * a copy-link action: RN Web's `<Modal>` (used directly by MediaViewer and,
 * via WebModal, by ImagesPreview) can wrap content in an animated
 * transformed layer, and react-navigation's `modalStyle` screens (see
 * navigation/helpers.tsx) animate in with `cardStyleInterpolator`'s
 * `translateY`. Nesting a local `<ToastProvider>` only worked around the
 * first case (TA-10845) — the second one still traps the toast, invisible
 * or displaced, no matter how deep the local ToastProvider sits. Portaling
 * straight to `document.body` (a sibling of all of that, not a descendant)
 * sidesteps both, since `document.body` itself has no transform.
 */
export const BottomToast = forwardRef<BottomToastHandle>((_props, ref) => {
  const [toast, setToast] = useState<{
    message: string;
    color?: string;
    icon?: 'warning';
  } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({
    show: (message, options) => {
      setToast({ message, color: options?.color, icon: options?.icon });
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        () => setToast(null),
        options?.duration ?? 3000
      );
    },
  }));

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  if (!toast || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role='alert'
      aria-live='assertive'
      aria-atomic='true'
      style={{
        // Structural/positioning values only — not design-system tokens,
        // since this raw <div> exists purely to escape any transformed
        // ancestor (see comment above); there's no Restyle Box/Text here
        // for theme.spacing/borderRadii to attach to via props.
        position: 'fixed',
        left: '50%',
        bottom: theme.spacing.xl,
        transform: 'translateX(-50%)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.xs,
        pointerEvents: 'none',
        // Visual values below mirror design-system tokens: textPrimary
        // stands in for the "dark surface" toast look (no dedicated
        // toast-surface color token exists), and borderRadii.l matches the
        // pill shape. The message itself renders through Restyle's Text
        // below instead of raw fontFamily/fontSize here.
        background: theme.colors.textPrimary,
        boxShadow: `0 4px 12px ${theme.colors.scrimDark20}`,
        paddingTop: theme.spacing.s,
        paddingBottom: theme.spacing.s,
        paddingLeft: theme.spacing.l,
        paddingRight: theme.spacing.l,
        borderRadius: theme.borderRadii.l,
      }}>
      {toast.icon === 'warning' && <WarningIcon />}
      <Text
        variant='labelSmall'
        style={
          toast.color ? { color: toast.color } : { color: theme.colors.white }
        }>
        {toast.message}
      </Text>
    </div>,
    document.body
  );
});
BottomToast.displayName = 'BottomToast';
